import moment from "moment";

import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { ShorthandDateSetter } from "@/Common/Components/Inputs/ShorthandDateSetter";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { Guid } from "@/Common/Models/Guid";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { ScreeningTestFrequencyOption, ScreeningTestReminderOption } from "@/Common/Models/UI/Inputs/DropDownOption";
import { Notice } from "@/DesignSystem/Molecules/Notice";
import { MedicalItemScreeningTest, ScreeningTestDueDate, ScreeningTestInterpretation, ScreeningTestStatus } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { LabRequest } from "@/Portfolio/Models/LabRequest";
import { MedicalRequestFormRowComponent } from "./MedicalRequestFormRow";

import styles from "./MedicalRequestPopupController.module.scss";

interface IScreeningTestFormComponentProps {
    model: MedicalItemScreeningTest;
    patientDoB: Date;

    disabled?: boolean;

    onRequestingStaffChange: (contactFacetId: Guid | null) => void;
    onFrequencyChanged: (dropdownId: string) => void;
    onReminderChanged: (dropdownId: string) => void;
    onStartAgeChanged: (startAge: number) => void;
    onEndAgeChanged: (endAge: number) => void;
    onStatusChanged: (lookupId: string) => void;
    onCompletionDateChanged: (datetime: Date) => void;
    onInterpretationChanged: (lookupId: string) => void;
    onDueDateChanged: (datetime: Date) => void;
    onNotesChanged: (note: string) => void;
}

class ScreeningTestFormComponentState {
    public screeningDueDate: ScreeningTestDueDate;

    public static create(model?: MedicalItemScreeningTest): ScreeningTestFormComponentState {
        let state = new ScreeningTestFormComponentState();
        state.screeningDueDate = null;
        if (model)
            state.screeningDueDate = new ScreeningTestDueDate(model.model.DueDate, "YYYY-MM-DD");
        return state;
    }
}

class ScreeningTestFormComponent extends BaseReactComponent<IScreeningTestFormComponentProps, ScreeningTestFormComponentState> {
    private id = "ScreeningTestFormComponent";

    constructor(props: IScreeningTestFormComponentProps) {
        super(props);
        this.state = ScreeningTestFormComponentState.create(props.model);
    }

    public componentDidUpdate(prevProps: IScreeningTestFormComponentProps): void {
        if (this.props.model !== prevProps.model) {
            this.setState(ScreeningTestFormComponentState.create(this.props.model));
        }
    }

    public render(): JSX.Element {
        const model = this.props.model;
        if (!model)
            return null;

        return (
            <div id={this.id}>
                {cacheStore.ClinicSettings.EnablePreventiveActivityRequestingStaff && (
                    <MedicalRequestFormRowComponent label="RequestingStaffLabel" dataCy="requestingStaffRow">
                        {this.renderRequestingStaff(model)}
                    </MedicalRequestFormRowComponent>
                )}

                <MedicalRequestFormRowComponent label="FrequencyLabel" dataCy="frequencyRow">
                    {this.renderFrequency(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="ReminderLabel" dataCy="reminderRow">
                    {this.renderReminder(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="StartAgeLabel" dataCy="startAgeRow">
                    {this.renderStartAge(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="EndAgeLabel" dataCy="endAgeRow">
                    {this.renderEndAge(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="StatusLabel" labelClassName={styles.statusRowForm} dataCy="statusRow">
                    {this.renderStatus(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="CompletionDateLabel" dataCy="completionDateRow" shouldHide={!model.isStatusCompleted()}>
                    {this.renderCompletionDate(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="InterpretationLabel" dataCy="interpretationRow" shouldHide={!model.isStatusCompleted()}>
                    {this.renderInterpretation(model)}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="CareElementDueDateLabel" dataCy="dueDateRow">
                    {this.renderDueDate()}
                </MedicalRequestFormRowComponent>

                <MedicalRequestFormRowComponent label="NotesLabel" dataCy="notesRow" className={styles.notesRow}>
                    {this.renderNotes(model)}
                </MedicalRequestFormRowComponent>

            </div>
        );
    }

    private get employeeList(): Immutable.List<SearchableItemModelT<Person>> {
        if (cacheStore.IsChristus() && this.props.model instanceof LabRequest) {
            return cacheStore.EmployeeCache
                .filter(p => !IsNullOrWhiteSpace(p.LicenceDSQ) && Employee.getIssuingOrgRule(p))
                .map(p => {
                    const person = Person.FromEmployee(p);

                    let displayName = person.NameWithPrefix;
                    if (!IsNullOrWhiteSpace(p.LicenceDSQ)) {
                        displayName += ` (${p.LicenceDSQ})`;
                    }

                    return new SearchableItemModelT<Person>(
                        displayName,
                        person.ContactId,
                        person
                    );
                })
                .toImmutable();
        }

        return cacheStore.EmployeeCache
            .filter(p => !Employee.shouldHaveSupervisorBasedOnTitle(p))
            .map(p => {
                const person = Person.FromEmployee(p, true, true);

                let displayName = person.NameWithPrefix;
                if (!IsNullOrWhiteSpace(person.Licence)) {
                    displayName += ` (${person.Licence})`;
                }

                return new SearchableItemModelT<Person>(
                    displayName,
                    person.ContactId,
                    person
                );
            })
            .toImmutable();
    }

    private renderRequestingStaff(model: MedicalItemScreeningTest): JSX.Element {
        const requestingStaffContactFacetId = model.getRequestingStaff();

        if (!this.props.disabled && !requestingStaffContactFacetId) {
            return (
                <TextboxControl
                    value=""
                    valid={Boolean(requestingStaffContactFacetId) && this.props.disabled}
                    autoCompleteItems={this.employeeList}
                    onAutoCompleteSelectCallBack={(_: any, model: Person) =>
                        this.props.onRequestingStaffChange?.(model.ContactFacetId)
                    }
                    onAutoCompleteSelectClearTextBox
                    placeholder=""
                />
            );
        }

        return (
            <div>
                <span className={styles.requestingStaffNamePrefix}>{model.getRequestingStaffNameWithPrefix()}</span>
                {!this.props.disabled && (
                    <span
                        className={styles.requestingStaffClearButton}
                        onClick={() => this.props.onRequestingStaffChange?.(null)}
                    >
                        X
                    </span>
                )}
            </div>
        );
    }

    private renderFrequency(model: MedicalItemScreeningTest): JSX.Element {
        return (
            <SelectLookupControl
                IncludeEmptyChoice={true}
                invalid={!model.model.FrequencyTypeDropDownId}
                Value={model.model.FrequencyTypeDropDownId}
                AvailableValues={ScreeningTestFrequencyOption.getAllAsCombobox()}
                disabled={this.props.disabled}
                Callback={(value: string) => this.handleOnFrequencyChanged(value)}
            />
        );
    }

    private renderReminder(model: MedicalItemScreeningTest): JSX.Element {
        return (
            <SelectLookupControl
                IncludeEmptyChoice={true}
                Value={model.model.ReminderTypeDropDownId}
                AvailableValues={ScreeningTestReminderOption.getAllAsCombobox()}
                disabled={this.props.disabled}
                Callback={(value: string) => this.props.onReminderChanged && this.props.onReminderChanged(value)}
            />
        );
    }

    private renderStartAge(model: MedicalItemScreeningTest): JSX.Element {
        const startAge = model.model.StartAge;
        return (
            <div className="row">
                <div className="col-xs-6">
                    <TextboxControl
                        value={startAge ? startAge.toString() : ""}
                        disabled={this.props.disabled}
                        onChange={value => this.handleOnStartAgeChanged(value)}
                        IsNumeric={true}
                        maxLength={3}
                    />
                </div>
                <div className="col-xs-6">
                    <p style={{ padding: "7.5px" }}>{cacheStore.GetString("YearOptionalSLabel")}</p>
                </div>
            </div>
        );
    }

    private renderEndAge(model: MedicalItemScreeningTest): JSX.Element {
        const endAge = model.model.EndAge;
        return (
            <div className="row">
                <div className="col-xs-6">
                    <TextboxControl
                        valid={model.isEndAgeValid()}
                        value={endAge ? endAge.toString() : ""}
                        disabled={this.props.disabled}
                        onChange={value => this.handleOnEndAgeChanged(value)}
                        IsNumeric={true}
                        maxLength={3}
                    />
                </div>
                <div className="col-xs-6">
                    <p style={{ padding: "7.5px" }}>{cacheStore.GetString("YearOptionalSLabel")}</p>
                </div>
            </div>
        );
    }

    private renderStatus(medicalItem: MedicalItemScreeningTest): JSX.Element {
        const screeningTest = medicalItem.model;
        return (
            <div>
                <SelectLookupControl
                    IncludeEmptyChoice={true}
                    invalid={!screeningTest.StatusLookupId}
                    Value={screeningTest.StatusLookupId}
                    AvailableValues={ScreeningTestStatus.GetAll().map(x => x.ToComboboxItem())}
                    disabled={this.props.disabled}
                    Callback={(value: string) => this.handleOnStatusChanged(value)}
                />
                {medicalItem.canShowStatusReminderText() &&
                    (cacheStore.ClinicSettings.EnablePreventiveActivityEnhancements
                        ? (
                            <div className={styles.notice}>
                                <Notice type={medicalItem.isInToDoPeriod() ? "warning" : "info"} message={medicalItem.getStatusReminderText()} />
                            </div>
                        )
                        : <p style={{ fontStyle: "italic" }}>{medicalItem.getStatusReminderText()}</p>)
                }
            </div>
        );
    }

    private renderCompletionDate(model: MedicalItemScreeningTest): JSX.Element {
        return (
            <div className="row">
                <div className="col-xs-6">
                    <TextboxControlWithDatePicker
                        id="CompletionDate"
                        isInvalid={!model.model.CompletionDate || moment(model.model.CompletionDate).isAfter()}
                        dateValue={model.model.CompletionDate}
                        textValue={model.model.CompletionDate ? moment(model.model.CompletionDate).format("YYYY-MM-DD") : ""}
                        onChange={(date: DateTimeWithPrecision) => this.props.onCompletionDateChanged && this.props.onCompletionDateChanged(date.value)}
                        fullWidth={true}
                        disabled={this.props.disabled}
                    />
                </div>
                {!this.props.disabled && (
                    <div className="col-xs-6" style={{ padding: "7.5px" }}>
                        <a onClick={() => this.props.onCompletionDateChanged && this.props.onCompletionDateChanged(moment().toDate())}>
                            {cacheStore.GetString("TodayLabel")}
                        </a>
                    </div>
                )}
            </div>
        );
    }

    private renderInterpretation(model: MedicalItemScreeningTest): JSX.Element {
        return (
            <SelectLookupControl
                IncludeEmptyChoice={true}
                Value={model.model.InterpretationLookupId}
                AvailableValues={ScreeningTestInterpretation.getAll().map(x => x.ToComboboxItem())}
                disabled={this.props.disabled}
                Callback={(value: string) => this.handleOnInterpretationChanged(value)}
            />
        );
    }

    private renderDueDate(): JSX.Element {
        return (
            <div className="row">
                <div className="col-xs-6">
                    <TextboxControlWithDatePicker
                        id="DueDate"
                        dateValue={this.state.screeningDueDate.dueDate}
                        textValue={this.state.screeningDueDate.dueDateText}
                        onChange={(date: DateTimeWithPrecision) => this.handleOnDueDateChange(date)}
                        disabled={this.props.disabled}
                        fullWidth={true}
                    />
                </div>
                {!this.props.disabled && (
                    <ShorthandDateSetter
                        initialDate={this.state.screeningDueDate.initialDueDate}
                        onClick={(newDate: Date) => this.handleOnAddTimeToDueDate(newDate)}
                        visibleTimeIntervals={ScreeningTestFrequencyOption.getAllIntervals()}
                    />
                )}
            </div>
        );
    }

    private renderNotes(model: MedicalItemScreeningTest): JSX.Element {
        return (
            <TextAreaResizable
                minHeightPx={100}
                maxHeightPx={300}
                id={"ScreeningNote_" + (model.model.ScreeningTestDefinitionId ? model.model.ScreeningTestDefinitionId : model.model.MedicalItemId)}
                text={model.model.Notes}
                isDisabled={this.props.disabled}
                setCursorStartOnFocus={false}
                resetTextOnIdChange={true}
                onChange={value => this.props.onNotesChanged?.(value)}
                verticalResizeOnly
            />
        );
    }

    private handleOnFrequencyChanged(frequecyDropdownId: string): void {
        this.props.onFrequencyChanged && this.props.onFrequencyChanged(frequecyDropdownId);
        this.changeDueDate(this.props.model.model.StatusLookupId, this.props.model.model.InterpretationLookupId, frequecyDropdownId);
    }

    private handleOnStartAgeChanged(value: any): void {
        if (!value || isNaN(value)) {
            this.props.onStartAgeChanged && this.props.onStartAgeChanged(null);
            return;
        }

        const startAge = Number(value);
        this.props.onStartAgeChanged && this.props.onStartAgeChanged(startAge);

        if (!MedicalItemScreeningTest.isPatientAgeLessThanStartAge(startAge, this.props.patientDoB))
            return;

        this.props.onStatusChanged && this.props.onStatusChanged(ScreeningTestStatus.Ineligible.LookupID);

        this.mutateState(s => {
            s.screeningDueDate.updateBaseOnStartAge(startAge, this.props.patientDoB);
        }, () => this.props.onDueDateChanged && this.props.onDueDateChanged(this.state.screeningDueDate.dueDate));
    }

    private handleOnEndAgeChanged(value: any): void {
        const endAge = !value ? null : Number(value);
        this.props.onEndAgeChanged && this.props.onEndAgeChanged(endAge);

        if (MedicalItemScreeningTest.isEndAgeValid(this.props.model.model.StartAge, endAge))
            this.changeDueDate(this.props.model.model.StatusLookupId, this.props.model.model.InterpretationLookupId, this.props.model.model.FrequencyTypeDropDownId, endAge);
    }

    private handleOnStatusChanged(statusLookupId: string): void {
        this.props.onStatusChanged && this.props.onStatusChanged(statusLookupId);
        this.changeDueDate(statusLookupId, this.props.model.model.InterpretationLookupId, this.props.model.model.FrequencyTypeDropDownId);
    }

    private handleOnInterpretationChanged(interpretationLookupId: string): void {
        this.props.onInterpretationChanged && this.props.onInterpretationChanged(interpretationLookupId);

        if (!cacheStore.ClinicSettings.EnablePreventiveActivityEnhancements)
            this.changeDueDate(this.props.model.model.StatusLookupId, interpretationLookupId, this.props.model.model.FrequencyTypeDropDownId);
    }

    private handleOnAddTimeToDueDate(newDate: Date): void {
        this.mutateState(
            s => s.screeningDueDate = s.screeningDueDate.updateDueDate(newDate),
            () => this.props.onDueDateChanged?.(this.state.screeningDueDate.dueDate)
        );
    }

    private handleOnDueDateChange(date: DateTimeWithPrecision): void {
        this.mutateState(
            s => {
                s.screeningDueDate.change(date);
            },
            () => this.props.onDueDateChanged && this.props.onDueDateChanged(this.state.screeningDueDate.dueDate)
        );
    }

    private changeDueDate(statusLookupId: string, interpretationLookupId: string, selectedFrequencyDropDownId: string, endAge?: number): void {
        const frequency = ScreeningTestFrequencyOption.fromDropDownId(selectedFrequencyDropDownId);
        const nextDueDate = this.state.screeningDueDate.calculateNextDueDate(statusLookupId, interpretationLookupId, frequency, this.props.model.model.CompletionDate);
        const patientDateOfBirth = this.props.patientDoB;
        const endAgeDate = endAge ? MedicalItemScreeningTest.calculateEndAgeDate(endAge, patientDateOfBirth) : this.props.model.getEndAgeDate(patientDateOfBirth);

        if (MedicalItemScreeningTest.isCompleted(statusLookupId) &&
            MedicalItemScreeningTest.isNormal(interpretationLookupId) &&
            MedicalItemScreeningTest.isDueDateAfterEndAgeDate(endAgeDate, nextDueDate)) {
            this.mutateState(
                s => {
                    s.screeningDueDate.clearDueDate();
                },
                () => {
                    this.props.onDueDateChanged && this.props.onDueDateChanged(null);
                    this.props.onReminderChanged && this.props.onReminderChanged("");
                }
            );
        }
        else {
            this.mutateState(
                s => {
                    s.screeningDueDate.updateBasedOnStatus(statusLookupId, interpretationLookupId, frequency, this.props.model.model.CompletionDate);
                },
                () => this.props.onDueDateChanged && this.props.onDueDateChanged(this.state.screeningDueDate.dueDate)
            );
        }
    }
}

export { ScreeningTestFormComponent };
